/**
 * This module emulates the localStorage API for environments that do not support it.
 * It provides methods to set, get, remove, and clear items in local storage.
 * It uses a simple in-memory object to store the data or a custom storage object if provided.
 * The module also provides utility methods to check storage API compliance and availability.
 */

import { isStorageAvailable } from './utils'
import { StorageWrapper } from './StorageWrapper'

export { StorageEmulator } from './StorageEmulator'
export { isStorageAvailable, checkStorageApiCompliance } from './utils'
export { StorageWrapper } from './StorageWrapper'
export { ExpirableStorage } from './ExpirableStorage'

/**
 * A global instance of StorageWrapper for localStorage.
 * Uses the browser's localStorage if available, otherwise falls back to StorageEmulator.
 */
export const storage = new StorageWrapper(
  typeof window !== 'undefined' ? window.localStorage : undefined
)

/**
 * A global instance of StorageWrapper for sessionStorage.
 * Uses the browser's sessionStorage if available, otherwise falls back to StorageEmulator.
 */
export const sessionStorage = new StorageWrapper(
  typeof window !== 'undefined' ? window.sessionStorage : undefined
)

/**
 * A global instance of StorageWrapper for localStorage.
 * Alias for `storage`.
 */
export const localStorage = storage

/**
 * Checks if the global `storage` instance is available.
 * @returns {boolean} True if available, false otherwise.
 */
export const storageAvailable = isStorageAvailable(storage)

/**
 * Checks if the global `sessionStorage` instance is available.
 * @returns {boolean} True if available, false otherwise.
 */
export const sessionStorageAvailable = isStorageAvailable(sessionStorage)

/**
 * Checks if the global `localStorage` instance is available.
 * @returns {boolean} True if available, false otherwise.
 */
export const localStorageAvailable = isStorageAvailable(localStorage)

export default storage
